<template>
    <!--
          Video Block that displays a thumbnail image, title and description.
          When the thumbnail image or "watch now" is clicked an iframe video will expand,
          hiding the description and showing a collapsible transcript.
        -->
    <div
      class="video-block"
      :class="{'video-block--expanded': showVideo}"
    >
      <!-- Thumbnail Image -->
      <button
        v-show="!showVideo"
        title="watch now"
        aria-label="watch now"
        class="video-block__thumbnail"
        @click="toggleVideo"
      >
        <div class="video-block__thumbnail-container">
          <div
            class="video-block__thumbnail-wrapper"
          >
            <img
              class="video-block__thumbnail-image"
              :src="thumbnailUrl"
              alt=""
              aria-label="Watch video"
            >
            <div class="video-block__thumbnail-icon-wrapper">
              <font-awesome-icon
                class="video-block__thumbnail-icon"
                icon="play"
              />
            </div>
          </div>
        </div>
      </button>
      <!-- Video -->
      <div
        v-show="showVideo"
        class="video-block__player"
      >
        <div class="video-block__player-wrapper">
          <pulse-loader
            :color="'#E4E6E2'"
            :size="'20px'"
            :margin="'10px'"
          />
          <iframe
            v-if="showVideo"
            :src="`${videoUrl}?autoplay=1`"
            :title="videoTitle"
            class="video-block__iframe"
            frameborder="0"
            allow="autoplay; fullscreen"
            allowfullscreen
          />
        </div>
        <!-- Transcript -->
        <div class="video-block__transcript">
          <button
            :id="`transcript-button-${id}`"
            class="video-block__transcript-button"
            :class="{'video-block__transcript-button--expanded': showTranscript}"
            aria-label="Toggle video transcript"
            :aria-expanded="showTranscript"
            @click="toggleTranscript"
          >
            <span class="video-block__transcript-label">
              <img
                class="video-block__transcript-icon"
                src="/web/themes/custom/education_base/assets/images/icon-transcript.svg"
                alt=""
              >
              <img
                class="video-block__transcript-icon-blue"
                src="/web/themes/custom/education_base/assets/images/icon-transcript-blue.svg"
                alt=""
              >
              <span>
                Video transcript
              </span>
            </span>
            <img
              class="video-block__transcript-close-btn"
              src="/web/themes/custom/education_base/assets/images/icon-close.svg"
              alt=""
            >
            <img
              class="video-block__transcript-close-btn-blue"
              src="/web/themes/custom/education_base/assets/images/icon-close-blue.svg"
              alt=""
            >
          </button>
          <div
            class="video-block__transcript-content"
            :class="{'video-block__transcript-content--expanded': showTranscript}"
            :aria-hidden="!showTranscript"
          >
            <div
              class="video-block__transcript-content-inner"
              :class="{'video-block__transcript-content-inner--expanded': showTranscript}"
              v-html="transcript"
            />
          </div>
        </div>
      </div>
      <!-- Title & Description -->
      <div
        class="video-block__content"
        :class="{'video-block__content--expanded': showVideo}"
      >
        <div class="video-block__title-wrapper">
          <h3
            class="video-block__title"
            :class="{'video-block__title--expanded': showVideo}"
            @click="toggleVideo"
          >
            {{ title }}
          </h3>
        </div>
        <button
          v-if="showVideo"
          class="video-block__close c-button c-button--link"
          aria-label="Close video"
          @click="toggleVideo"
        >
          <img
            src="/web/themes/custom/education_base/assets/images/icon-close.svg"
            class="video-block__close-btn"
            alt=""
          >
          <img
            src="/web/themes/custom/education_base/assets/images/icon-close-blue.svg"
            class="video-block__close-btn-blue"
            alt=""
          >
        </button>
        <div
          v-if="!showVideo"
          class="video-block__description"
        >
          {{ description }}
        </div>
        <div class="video-block__content-footer">
          <!-- eslint-disable-next-line max-len -->
          <button
            v-if="!showVideo"
            class="
                  video-block__watch-button
                  c-button
                  c-button--link
                  c-button--bold
                  c-button--uppercase
                "
            @click="toggleVideo"
          >
            watch now
          </button>
          <strong class="video-block__duration">
            {{ duration }}m
          </strong>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { PulseLoader } from 'vue-spinner/dist/vue-spinner.min';
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
  
  export default {
    name: 'VideoBlock',
    components: {
      PulseLoader,
      FontAwesomeIcon,
    },
    props: {
      /**
           * A unique number used to aid accessibility.
           */
      id: {
        type: Number,
        required: true,
      },
      /**
           * Title for the block
           */
      title: {
        type: String,
        required: true,
      },
      /**
           * Description for the block
           */
      description: {
        type: String,
        required: true,
      },
      /**
           * Video duration
           */
      duration: {
        type: String,
        required: true,
      },
      /**
           * Url for the video thumbnail image
           */
      thumbnailUrl: {
        type: String,
        required: false,
        default: '',
      },
      /**
           * Url for video to be embeded in an iframe
           */
      videoUrl: {
        type: String,
        required: true,
      },
      /**
           * Title for the video in the iframe, can be different to the block title
           */
      videoTitle: {
        type: String,
        required: false,
        default: '',
      },
      /**
           * Transcript for the video
           */
      transcript: {
        type: String,
        required: true,
        default: '',
      },
    },
    data() {
      return {
        showVideo: false,
        showTranscript: false,
      };
    },
    mounted() {},
    methods: {
      /**
           * Used for hiding and showing the video iframe
           */
      toggleVideo() {
        this.showVideo = !this.showVideo;
      },
      /**
           * Used for expanding and collapsing the video transcript
           */
      toggleTranscript() {
        this.showTranscript = !this.showTranscript;
      },
    },
  };
  </script>